import { act_payment } from '@lc//interface/payment/action';
import { act_com } from '@lc/action/cardType/Common';
import {
	handSizePlus2,
	// summitCarbonDump
} from '@lc/const/modifiers';
import { GameState } from '@lc/interface/gameState/types';
import { act_player } from '@lc/interface/player/action';
import { getCurrentSeasonDeck } from '@lc/lib/gamestate_helper';
import { getPlayerStateWithId } from '@lc/lib/player_helper';
import { calculateFactoryCost } from '@lc/lib/playermat_helper';

class SummitAction {
	addDonation(
		gameState: GameState,
		playerId: number,
		amount: number,
		energyColor: string = ''
	) {
		if (amount === 0) {
			return;
		}
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		if (energyColor === '') {
			// default to player's energyColor if none specified
			energyColor = pState.energyColor;
		}

		for (let i = 0; i < amount; i++) {
			pState.donationCupArr.push(energyColor);
		}
	}

	buyFactoryHalfOff(
		playerData: any,
		gameState: GameState,
		dir: string = 'down'
	) {
		const { playerId, paymentObject } = playerData;
		const playerStateArray = gameState.playerStateArray;
		const pState = getPlayerStateWithId(playerStateArray, playerId);

		const factoryCost = calculateFactoryCost(pState);

		let factoryCostReduced = 0;
		if (dir === 'up') {
			factoryCostReduced = Math.ceil(factoryCost / 2);
		} else if (dir === 'down') {
			factoryCostReduced = Math.floor(factoryCost / 2);
		}

		if (
			act_payment.getPaymentObjectTotal(paymentObject) !==
			factoryCostReduced
		) {
			return false;
		}

		act_player.buyFactory(pState);

		act_payment.payForAction(pState, paymentObject);
		return true;
	}

	energyPerEquipment(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		this.addDonation(gameState, playerId, pState.equipment.length * 2);
		return true;
	}

	failedNeighborContracts(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const players = gameState.playerStateArray;
		// const pState = getPlayerStateWithId(players, playerId);

		const neighborIdArrTmp = act_com.createNeighborIdArray(
			playerData,
			players
		);
		const neighborIdArr = [
			neighborIdArrTmp[0] || 0,
			neighborIdArrTmp[2] || 0,
		];

		const donationAmount =
			players[neighborIdArr[0] || 0].failedContracts.length ||
			0 + players[neighborIdArr[2] || 0].failedContracts.length ||
			0;

		this.addDonation(gameState, playerId, donationAmount);
		return true;
	}

	handSizePlus2(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		pState.modifiers.push(handSizePlus2);

		const currDeck = getCurrentSeasonDeck(gameState);

		if (currDeck.length > 0)
			pState.seasonalCards.push(currDeck.shift() || -1);
		if (currDeck.length > 0)
			pState.seasonalCards.push(currDeck.shift() || -1);
	}

	highestPR(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const playerStateArray = gameState.playerStateArray;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		const currPlayerPR = pState.prArr.length;
		let maxOtherPlayerPR = 0;

		for (let i = 0; i < playerStateArray.length; i++) {
			if (i !== playerId) {
				maxOtherPlayerPR = Math.max(
					playerStateArray[i].prArr.length,
					maxOtherPlayerPR
				);
			}
		}

		if (currPlayerPR > maxOtherPlayerPR) {
			this.addDonation(gameState, playerId, 8);
		} else if (currPlayerPR === maxOtherPlayerPR) {
			this.addDonation(gameState, playerId, 4);
		}

		return true;
	}

	lowestFootprint(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const playerStateArray = gameState.playerStateArray;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		const currPlayerCarbonFootprint = pState.carbonFootprint;
		let lowestOtherCarbonFootprint = Infinity;
		let maxOtherCarbonFoorprint = 0;

		for (let i = 0; i < playerStateArray.length; i++) {
			if (i !== playerId) {
				lowestOtherCarbonFootprint = Math.min(
					playerStateArray[i].carbonFootprint,
					lowestOtherCarbonFootprint
				);
				maxOtherCarbonFoorprint = Math.max(
					playerStateArray[i].carbonFootprint,
					maxOtherCarbonFoorprint
				);
			}
		}

		if (currPlayerCarbonFootprint < lowestOtherCarbonFootprint) {
			this.addDonation(gameState, playerId, maxOtherCarbonFoorprint);
		}

		return true;
	}

	mostBatteries(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const playerStateArray = gameState.playerStateArray;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		const currPlayerBatteries = pState.batteries;
		let maxOtherBatteries = 0;

		for (let i = 0; i < playerStateArray.length; i++) {
			if (i !== playerId) {
				maxOtherBatteries = Math.max(
					playerStateArray[i].factories,
					maxOtherBatteries
				);
			}
		}

		if (currPlayerBatteries > maxOtherBatteries) {
			this.addDonation(gameState, playerId, 5);
		} else if (currPlayerBatteries === maxOtherBatteries) {
			this.addDonation(gameState, playerId, 2);
		}

		return true;
	}

	mostFactories(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const playerStateArray = gameState.playerStateArray;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		const currPlayerFactories = pState.factories;
		let maxOtherFactories = 0;

		for (let i = 0; i < playerStateArray.length; i++) {
			if (i !== playerId) {
				maxOtherFactories = Math.max(
					playerStateArray[i].factories,
					maxOtherFactories
				);
			}
		}

		if (currPlayerFactories > maxOtherFactories) {
			this.addDonation(gameState, playerId, 5);
		} else if (currPlayerFactories === maxOtherFactories) {
			this.addDonation(gameState, playerId, 2);
		}

		return true;
	}

	// if no failed contracts, +2 energy per completed contract
	noFailedContracts(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		if (pState.failedContracts.length === 0) {
			const amount = pState.completedContracts.length * 2;
			this.addDonation(gameState, playerId, amount);
		}

		return true;
	}

	threeCompletedContracts(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		if (pState.completedContracts.length >= 3) {
			this.addDonation(gameState, playerId, 5);
		}

		return true;
	}

	// +1 energy per card in your hand at winter summit
	winterSummitHandSizeEnergy(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		const amount = pState.seasonalCards.length;
		this.addDonation(gameState, playerId, amount);
		return true;
	}
}

export const act_summit = new SummitAction();
